import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, Eye, Copy, ExternalLink, ArrowLeft,
  CheckCircle2, EyeOff, Archive,
} from "lucide-react";
import { coursesList, courseGet, courseCreate, courseUpdate, courseDelete } from "../adminApi";
import MediaUploader from "../components/MediaUploader";

/* ---------- array <-> text helpers ---------- */
const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (Array.isArray(a) ? a : []).join("\n");

const parseSyllabus = (text) =>
  (text || "")
    .split(/\n\s*\n/)
    .map((block) => {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      if (!lines.length) return null;
      const title = lines[0].replace(/^[-•*]\s*/, "");
      const points = lines.slice(1).map((l) => l.replace(/^[-•*]\s*/, "").trim()).filter(Boolean);
      return { title, points };
    })
    .filter(Boolean);
const stringifySyllabus = (arr) =>
  (arr || []).map((m) => [m.title, ...(m.points || []).map((p) => `- ${p}`)].join("\n")).join("\n\n");

const parseFaqs = (text) =>
  (text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.includes("|"))
    .map((l) => {
      const i = l.indexOf("|");
      return { q: l.slice(0, i).trim(), a: l.slice(i + 1).trim() };
    })
    .filter((f) => f.q && f.a);
const stringifyFaqs = (arr) => (arr || []).map((f) => `${f.q} | ${f.a}`).join("\n");

const MODES = ["Online", "Offline", "Both"];
const slugPreview = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-");
const STATUS_BADGE = {
  published: "bg-green-100 text-green-700",
  draft: "bg-amber-100 text-amber-700",
  archived: "bg-slate-200 text-slate-600",
};

function Field({ label, value, onChange, type = "text", rows, placeholder, options, hint, testId }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {options ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange">
          <option value="">Select</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows || 3} placeholder={placeholder} data-testid={testId} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
      )}
      {hint && <span className="text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="col-span-full mt-4 border-b border-slate-200 pb-1 text-sm font-bold uppercase tracking-wide text-slate-500">{children}</h3>
);

const emptyForm = {
  name: "", category: "", shortDescription: "", detailedDescription: "",
  thumbnail: "", banner: "", duration: "", mode: "", eligibility: "", fees: "",
  trainerName: "", trainerTitle: "", trainerBio: "", trainerImage: "",
  overview: "", whyLearn: "", skills: "", highlights: "", outcomes: "", careers: "",
  whyChoose: "", projects: "", syllabusText: "", faqsText: "",
  purchaseUrl: "", primaryButtonText: "", secondaryButtonText: "",
  demoType: "form", demoUrl: "", downloadSyllabusUrl: "",
  seoTitle: "", seoDescription: "", ogImage: "", canonical: "",
  featured: false, order: 0,
};

const toForm = (c) => ({
  ...emptyForm, ...c,
  skills: arrToLines(c.skills), highlights: arrToLines(c.highlights),
  outcomes: arrToLines(c.outcomes), careers: arrToLines(c.careers),
  whyChoose: arrToLines(c.whyChoose), projects: arrToLines(c.projects),
  syllabusText: stringifySyllabus(c.syllabus), faqsText: stringifyFaqs(c.faqs),
});

const toPayload = (f) => {
  const { syllabusText, faqsText, ...rest } = f;
  return {
    ...rest,
    skills: linesToArr(f.skills), highlights: linesToArr(f.highlights),
    outcomes: linesToArr(f.outcomes), careers: linesToArr(f.careers),
    whyChoose: linesToArr(f.whyChoose), projects: linesToArr(f.projects),
    syllabus: parseSyllabus(syllabusText), faqs: parseFaqs(faqsText),
    order: parseInt(f.order || 0, 10) || 0,
  };
};

export default function AdminCourses() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?} form
  const [saving, setSaving] = useState(false);

  const load = () => coursesList().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...emptyForm });
  const openEdit = async (c) => {
    try { setEditing(toForm(await courseGet(c.id))); }
    catch { toast.error("Failed to load course."); }
  };
  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v }));

  const save = async (overrideStatus) => {
    if (!editing.name?.trim()) { toast.error("Course name is required."); return; }
    setSaving(true);
    try {
      const payload = toPayload(editing);
      if (overrideStatus) payload.status = overrideStatus;
      else payload.status = editing.status || "draft";
      const saved = editing.id
        ? await courseUpdate(editing.id, payload)
        : await courseCreate(payload);
      toast.success("Saved.");
      setEditing(toForm(saved));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Save failed.");
    } finally { setSaving(false); }
  };

  const quickStatus = async (c, status) => {
    try { await courseUpdate(c.id, { status }); toast.success(`Course ${status}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}" permanently? Consider Archive instead.`)) return;
    try { await courseDelete(c.id); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const copyLink = (slug) => {
    navigator.clipboard?.writeText(`${window.location.origin}/courses/${slug}`);
    toast.success("Page link copied.");
  };

  /* ---------------- FORM VIEW ---------------- */
  if (editing) {
    const f = editing;
    const slug = f.slug;
    return (
      <div data-testid="admin-course-form" className="mx-auto max-w-3xl">
        <button onClick={() => setEditing(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back to courses
        </button>
        <h1 className="text-2xl font-bold text-slate-900">{f.id ? "Edit Course" : "Add Course"}</h1>

        {f.id && slug && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm">
            <span className="text-slate-500">Landing URL:</span>
            <code className="rounded bg-white px-2 py-1 text-navy">/courses/{slug}</code>
            <button onClick={() => copyLink(slug)} data-testid="course-copy-link" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-orange"><Copy className="h-3.5 w-3.5" /> Copy</button>
            <a href={`/admin/courses/${f.id}/preview`} target="_blank" rel="noreferrer" data-testid="course-preview" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-orange"><Eye className="h-3.5 w-3.5" /> Preview</a>
          </div>
        )}
        {!f.id && f.name?.trim() && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm" data-testid="course-slug-preview">
            <span className="text-slate-500">Landing URL will be:</span>
            <code className="rounded bg-white px-2 py-1 text-navy">/courses/{slugPreview(f.slug || f.name)}</code>
            <span className="text-xs text-slate-400">(auto-generated — save to confirm)</span>
          </div>
        )}

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <SectionTitle>Basic Information</SectionTitle>
          <Field label="Course Name *" value={f.name} onChange={set("name")} testId="course-name" hint="URL slug is auto-generated from this name." />
          <Field label="Course Category" value={f.category} onChange={set("category")} testId="course-category" placeholder="e.g. Cloud & DevOps" />
          <Field label="Edit URL Slug (optional)" value={f.slug || ""} onChange={set("slug")} testId="course-slug" hint="Leave as-is to keep auto-generated. Changing a published slug keeps a redirect." />
          <Field label="Duration" value={f.duration} onChange={set("duration")} placeholder="e.g. 3 Months" />
          <Field label="Learning Mode" value={f.mode} onChange={set("mode")} options={MODES} />
          <Field label="Course Fees (optional)" value={f.fees} onChange={set("fees")} placeholder="e.g. ₹30,000" />
          <Field label="Short Description" value={f.shortDescription} onChange={set("shortDescription")} type="textarea" rows={2} testId="course-short" />
          <Field label="Eligibility / Prerequisites" value={f.eligibility} onChange={set("eligibility")} type="textarea" rows={2} />
          <div className="md:col-span-2"><Field label="Detailed Description" value={f.detailedDescription} onChange={set("detailedDescription")} type="textarea" rows={4} /></div>
          <MediaUploader label="Course Thumbnail" value={f.thumbnail} onChange={set("thumbnail")} accept="image/*" testId="course-thumbnail" hint="Recommended 1280×720px (16:9). Full image is shown without cropping, so keep the ASAI logo and text inside the frame." />
          <MediaUploader label="Course Banner Image" value={f.banner} onChange={set("banner")} accept="image/*" testId="course-banner" hint="Recommended 1600×500px wide banner for the course landing page header." />

          <SectionTitle>Landing Page Content (empty sections are hidden on the page)</SectionTitle>
          <div className="md:col-span-2"><Field label="Course Overview" value={f.overview} onChange={set("overview")} type="textarea" rows={3} /></div>
          <div className="md:col-span-2"><Field label="Why Learn This Course?" value={f.whyLearn} onChange={set("whyLearn")} type="textarea" rows={3} /></div>
          <Field label="Skills & Technologies (one per line)" value={f.skills} onChange={set("skills")} type="textarea" rows={4} />
          <Field label="Course Highlights (one per line)" value={f.highlights} onChange={set("highlights")} type="textarea" rows={4} />
          <Field label="Learning Outcomes (one per line)" value={f.outcomes} onChange={set("outcomes")} type="textarea" rows={4} />
          <Field label="Career Opportunities (one per line)" value={f.careers} onChange={set("careers")} type="textarea" rows={4} />
          <Field label="Practical Projects (one per line)" value={f.projects} onChange={set("projects")} type="textarea" rows={4} />
          <Field label="Why Choose ASAI (one per line)" value={f.whyChoose} onChange={set("whyChoose")} type="textarea" rows={4} />
          <div className="md:col-span-2"><Field label="Module-wise Syllabus" value={f.syllabusText} onChange={set("syllabusText")} type="textarea" rows={6} hint="Module title on its own line, then bullet points with '-'. Separate modules with a blank line." /></div>
          <div className="md:col-span-2"><Field label="FAQs" value={f.faqsText} onChange={set("faqsText")} type="textarea" rows={5} hint="One per line as:  Question | Answer" /></div>

          <SectionTitle>Trainer Information</SectionTitle>
          <Field label="Trainer Name" value={f.trainerName} onChange={set("trainerName")} />
          <Field label="Trainer Title / Role" value={f.trainerTitle} onChange={set("trainerTitle")} />
          <div className="md:col-span-2"><Field label="Trainer Bio" value={f.trainerBio} onChange={set("trainerBio")} type="textarea" rows={3} /></div>
          <MediaUploader label="Trainer Photo" value={f.trainerImage} onChange={set("trainerImage")} accept="image/*" />

          <SectionTitle>Buttons & Purchase / Demo</SectionTitle>
          <Field label="Purchase / Payment URL" value={f.purchaseUrl} onChange={set("purchaseUrl")} testId="course-purchase-url" placeholder="https://..." hint="External https checkout link. If empty, the Enroll button is hidden." />
          <Field label="Primary Button Text" value={f.primaryButtonText} onChange={set("primaryButtonText")} placeholder="Enroll Now" />
          <Field label="Secondary Button Text" value={f.secondaryButtonText} onChange={set("secondaryButtonText")} placeholder="Book Free Demo" />
          <Field label="Demo / Enquiry Destination" value={f.demoType} onChange={set("demoType")} options={["form", "url"]} hint="'form' = built-in enquiry (course preselected). 'url' = external booking link below." />
          {f.demoType === "url" && <Field label="External Demo/Booking URL" value={f.demoUrl} onChange={set("demoUrl")} placeholder="https://..." />}
          <Field label="Download Syllabus URL (optional)" value={f.downloadSyllabusUrl} onChange={set("downloadSyllabusUrl")} placeholder="https://..." />

          <SectionTitle>SEO & Display</SectionTitle>
          <Field label="SEO Title" value={f.seoTitle} onChange={set("seoTitle")} />
          <Field label="Canonical URL (optional)" value={f.canonical} onChange={set("canonical")} placeholder="https://..." />
          <div className="md:col-span-2"><Field label="Meta Description" value={f.seoDescription} onChange={set("seoDescription")} type="textarea" rows={2} /></div>
          <MediaUploader label="Social Sharing Image (OG)" value={f.ogImage} onChange={set("ogImage")} accept="image/*" />
          <Field label="Display Order (lower shows first)" value={f.order} onChange={set("order")} type="number" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured")(e.target.checked)} /> Featured course</label>
        </div>

        <div className="sticky bottom-0 mt-6 flex flex-wrap gap-3 border-t border-slate-200 bg-white py-4">
          <button onClick={() => save("draft")} disabled={saving} data-testid="course-save-draft" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save as Draft
          </button>
          <button onClick={() => save("published")} disabled={saving} data-testid="course-publish" className="inline-flex items-center gap-2 rounded-lg bg-orange px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            <CheckCircle2 className="h-4 w-4" /> Save & Publish
          </button>
          {f.purchaseUrl ? null : <span className="self-center text-xs text-amber-600">⚠ No Purchase URL set — the Enroll button will be hidden on the landing page.</span>}
        </div>
      </div>
    );
  }

  /* ---------------- LIST VIEW ---------------- */
  return (
    <div data-testid="admin-courses">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Course Management</h1>
          <p className="text-sm text-slate-500">Add courses — each gets an auto-generated landing page at /courses/&lt;slug&gt;. Published courses appear in the nav dropdown &amp; Courses page.</p>
        </div>
        <button onClick={openNew} data-testid="course-add" className="inline-flex items-center gap-2 rounded-lg bg-orange px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add Course</button>
      </div>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-orange" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-slate-400">No courses yet. Click “Add Course” to create your first one.</div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} data-testid="course-row" className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{c.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>{c.status}</span>
                  {c.featured && <span className="rounded-full bg-orange/10 px-2 py-0.5 text-xs font-medium text-orange">Featured</span>}
                </div>
                <div className="text-xs text-slate-400">/courses/{c.slug} {c.category ? `· ${c.category}` : ""}</div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {c.status === "published" ? (
                  <a href={`/courses/${c.slug}`} target="_blank" rel="noreferrer" title="View live" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><ExternalLink className="h-4 w-4" /></a>
                ) : (
                  <a href={`/admin/courses/${c.id}/preview`} target="_blank" rel="noreferrer" title="Preview draft" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Eye className="h-4 w-4" /></a>
                )}
                <button onClick={() => openEdit(c)} title="Edit" data-testid={`course-edit-${c.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                {c.status !== "published" ? (
                  <button onClick={() => quickStatus(c, "published")} title="Publish" className="rounded-lg p-2 text-green-600 hover:bg-green-50"><CheckCircle2 className="h-4 w-4" /></button>
                ) : (
                  <button onClick={() => quickStatus(c, "draft")} title="Unpublish" className="rounded-lg p-2 text-amber-600 hover:bg-amber-50"><EyeOff className="h-4 w-4" /></button>
                )}
                {c.status !== "archived" && <button onClick={() => quickStatus(c, "archived")} title="Archive" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Archive className="h-4 w-4" /></button>}
                <button onClick={() => remove(c)} title="Delete" className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
